import React from 'react'
import { StarFill } from 'react-bootstrap-icons'
import { format, isAfter, isBefore } from 'date-fns'
import {
  getTimelineWeekStart,
  isTimelineMonday,
  isTimelineToday,
  isTimelineWeekend,
  normalizeTimelineDate,
  TIMELINE_DATE_COLUMN_WIDTH,
} from '../../../contexts/TimelineContext'

export const groupTimelineDatesByWeek = (dateList) => {
  const weeks = []
  let currentWeek = []

  dateList.forEach((date) => {
    if (
      currentWeek.length === 0 ||
      getTimelineWeekStart(date).getTime() === getTimelineWeekStart(currentWeek[0] || date).getTime()
    ) {
      currentWeek.push(date)
    } else {
      weeks.push(currentWeek)
      currentWeek = [date]
    }
  })

  if (currentWeek.length > 0) {
    weeks.push(currentWeek)
  }

  return weeks
}

export const getTimelineItemVisualWidth = (asset, timelineState) => {
  if (String(asset.id).startsWith('temp-')) {
    return TIMELINE_DATE_COLUMN_WIDTH * 4
  }

  if (!asset.end_date) {
    return TIMELINE_DATE_COLUMN_WIDTH
  }

  return Math.max(
    (timelineState.getDaysBetween(asset.start_date, asset.end_date) + 1) * TIMELINE_DATE_COLUMN_WIDTH,
    TIMELINE_DATE_COLUMN_WIDTH,
  )
}

export const isTimelineDateOutsideRange = (date, startDate, endDate) => {
  const normalizedDate = normalizeTimelineDate(date)
  const normalizedStartDate = normalizeTimelineDate(startDate)
  const normalizedEndDate = normalizeTimelineDate(endDate)

  if (!normalizedDate) return false
  if (normalizedStartDate && isBefore(normalizedDate, normalizedStartDate)) return true
  if (normalizedEndDate && isAfter(normalizedDate, normalizedEndDate)) return true
  return false
}

export const getTimelineDayState = (date, holidayName, { isOutsideTimelineRange = false } = {}) => {
  const isToday = isTimelineToday(date)
  const isNonWorkingDay = isTimelineWeekend(date) || Boolean(holidayName)

  return {
    isToday,
    isNonWorkingDay,
    isOutsideTimelineRange,
  }
}

export const timelineDayBackgroundClass = ({ isToday, isNonWorkingDay, isOutsideTimelineRange }) => {
  if (isOutsideTimelineRange) return 'bg-rb-surface-muted dark:bg-rb-dark-mode-background'
  if (isToday) return 'bg-rb-green/20 dark:bg-rb-green/15'
  if (isNonWorkingDay) return 'bg-rb-surface-muted dark:bg-rb-dark-mode-background'
  return 'bg-white dark:bg-rb-dark-mode'
}

export function TimelineDateHeader({ date, holidayName, isOutsideTimelineRange = false }) {
  const isMonday = isTimelineMonday(date)
  const { isToday, isNonWorkingDay } = getTimelineDayState(date, holidayName, { isOutsideTimelineRange })

  return (
    <div className="relative flex h-12 items-center justify-center">
      {isMonday ? (
        <span className="absolute left-1 top-1 text-[10px] leading-3 text-rb-lighter-grey dark:text-gray-400">Mon</span>
      ) : null}
      {holidayName ? (
        <span
          aria-label={holidayName}
          className="absolute right-1 top-1 inline-flex h-3 w-3 items-center justify-center rounded-full bg-red-100 text-red-400 dark:bg-red-950/50 dark:text-red-300"
        >
          <StarFill className="h-1.5 w-1.5" />
        </span>
      ) : null}
      <span
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm ${
          isOutsideTimelineRange
            ? 'text-rb-lighter-grey dark:text-gray-400'
            : isToday
            ? 'bg-rb-green font-semibold text-rb-black dark:text-rb-dark-mode'
            : holidayName
              ? 'text-red-500 dark:text-red-300'
              : isNonWorkingDay
                ? 'text-rb-lighter-grey dark:text-gray-400'
                : 'text-rb-black dark:text-white'
        }`}
      >
        {format(date, 'd')}
      </span>
    </div>
  )
}

export function TimelineShowTodayButton({
  onClick,
  className = 'fixed bottom-4 right-4 z-30',
  startDate,
  endDate,
  today = new Date(),
}) {
  const normalizedToday = normalizeTimelineDate(today)
  const normalizedStartDate = normalizeTimelineDate(startDate)
  const normalizedEndDate = normalizeTimelineDate(endDate)
  const isTodayInRange = Boolean(
    normalizedToday &&
      normalizedStartDate &&
      normalizedEndDate &&
      !isBefore(normalizedToday, normalizedStartDate) &&
      !isAfter(normalizedToday, normalizedEndDate),
  )

  if (!isTodayInRange) return null

  return (
    <button
      type="button"
      aria-label="Show today"
      onClick={onClick}
      className={`${className} flex items-center gap-2 rounded-lg border border-rb-widget-border bg-white px-2.5 py-2 text-xs font-medium text-rb-black shadow-sm transition hover:shadow-md dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white`}
    >
      <span>Show today</span>
    </button>
  )
}
